import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { LoadMoreFooter } from '@/components/LoadMoreFooter';
import { useProductsLoadMore, useProductsStats } from '@/features/product/hooks';
import {
  useProductListControls,
  validateProductListSearch,
} from '@/features/product/list-controls';
import {
  ListEmptyState,
  ListErrorState,
  ListLoadingState,
} from '@/features/product/components/table-kit';
import { ClinicalReportFinderTable } from '@/features/clinical-report/components/ClinicalReportFinderTable';
import { PageHero } from '@/components/layout/PageHero';

/**
 * Clinical Study Reports — list page (table view).
 *
 * Same panel + table layout as the predicate finder, strategy, and protocol
 * list pages. Clicking Run dispatches a clinical-report run and navigates to
 * /clinical-reports/$productId for polling + rendering.
 */
export const Route = createFileRoute('/clinical-reports')({
  validateSearch: validateProductListSearch,
  component: ClinicalReportsPage,
});

function ClinicalReportsPage() {
  // Keyset-paginated list (10/page, PER-PAGE Load more) + server-accurate
  // tile counts — the tiles must not be computed from loaded rows only.
  // Search + sort are SERVER params (debounced q; sort key + direction) so
  // results cover the whole roster, not just the loaded rows.
  const searchParams = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const {
    searchInput,
    setSearchInput,
    q,
    sort,
    onSort,
    productType,
    onProductType,
    filtering,
  } = useProductListControls(searchParams, navigate);
  const { visible, hasMore, isFetchingMore, loadMore, isLoading, isError, totalCount } =
    useProductsLoadMore(10, { q, sort, productType, dispatchReady: true });
  const statsQuery = useProductsStats();
  const stats = {
    // dispatch_ready, not total: this table lists dispatch-ready products
    // only, and a badge that counts rows the table withholds is what made
    // the same "Products" number differ from tab to tab.
    total: statsQuery.data?.dispatch_ready ?? 0,
    analyzed: statsQuery.data?.analyzed['report'] ?? 0,
    running: statsQuery.data?.running['report'] ?? 0,
    marketUpdates: statsQuery.data?.market_updates['report'] ?? 0,
  };

  return (
    <>
      <PageHero
        title="Clinical Study Reports"
        subtitle="Generate a regulator-aligned Clinical Study Report (CSR) — ICH E3 structure, efficacy + safety findings, statistical analysis — grounded in your product's clinical evidence."
      />

      <main className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-20">
        {isLoading && <ListLoadingState />}
        {isError && <ListErrorState />}
        {!isLoading && !isError && visible.length === 0 && !filtering && (
          <ListEmptyState
            title="No products to analyze yet."
            body="Register a product to generate a Clinical Study Report for it."
          >
            <Button variant="brand" asChild>
              <a href="/product-account">Create a product</a>
            </Button>
          </ListEmptyState>
        )}
        {!isLoading && !isError && (visible.length > 0 || filtering) && (
          <>
            <ClinicalReportFinderTable
              products={visible}
              stats={stats}
              search={searchInput}
              onSearch={setSearchInput}
              sort={sort}
              onSort={onSort}
              productType={productType}
              onProductType={onProductType}
            />
            <LoadMoreFooter
              loaded={visible.length}
              total={totalCount}
              hasMore={hasMore}
              isFetching={isFetchingMore}
              onLoadMore={loadMore}
            />
          </>
        )}
      </main>
    </>
  );
}


